import React, { useState, useMemo } from 'react';
import { useApp, hasPrefix, previewDocNo } from '../../context/AppContext';
import { useToast } from '../../context/ToastContext';
import DataTable from '../common/DataTable';
import Modal from '../common/Modal';
import FormField, { ErrorSummary } from '../common/FormField';
import PrintPreview from '../common/PrintPreview';
import { Plus, Trash2, Pencil, Printer } from 'lucide-react';
import { formatDate, today } from '../../utils/helpers';
import { validators, validateForm } from '../../utils/validation';

const CONSUMPTION_HEADS = ['Stationary', 'Machine Maintenance', 'Building Maintenance', 'General', 'Other'];

const emptyLine = () => ({ itemId: '', qty: 0 });
const emptyForm = () => ({ date: today(), consumptionHead: 'Stationary', issuedTo: '', narration: '', items: [emptyLine()] });

export default function StockIssue() {
  const { state, dispatch } = useApp();
  const { addToast } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [printDoc, setPrintDoc] = useState(null);

  const getItem = (id) => state.items.find(i => i.id === id);
  const itemName = (id) => getItem(id)?.name || '-';

  // Only purchase items can be issued for consumption.
  const issuableItems = state.items.filter(i => i.isActive !== false && i.isPurchaseItem !== false);

  // When editing, the old issue is reversed on save, so that item's own qty is available again.
  const availableFor = (itemId) => {
    let avail = getItem(itemId)?.currentStock || 0;
    if (editId) {
      const old = state.stockIssues.find(s => s.id === editId);
      (old?.items || []).forEach(li => { if (li.itemId === itemId) avail += Number(li.qty) || 0; });
    }
    return avail;
  };

  const errors = useMemo(() => {
    const rules = { date: [validators.required(form.date, 'Date')] };
    const valid = form.items.filter(l => l.itemId && Number(l.qty) > 0);
    if (valid.length === 0) rules.items = ['Add at least one item to issue'];
    form.items.forEach((l, i) => {
      if (l.itemId && Number(l.qty) > 0 && Number(l.qty) > availableFor(l.itemId)) {
        rules[`item_${i}`] = [`Not enough stock for ${itemName(l.itemId)} (available ${availableFor(l.itemId)})`];
      }
    });
    const { errors } = validateForm(rules);
    return errors;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, editId, state]);

  const hasErrors = Object.values(errors).some(e => e);

  const setLine = (idx, patch) =>
    setForm(f => ({ ...f, items: f.items.map((l, i) => i === idx ? { ...l, ...patch } : l) }));
  const addLine = () => setForm(f => ({ ...f, items: [...f.items, emptyLine()] }));
  const removeLine = (idx) =>
    setForm(f => ({ ...f, items: f.items.length > 1 ? f.items.filter((_, i) => i !== idx) : f.items }));

  const cleanLines = (lines) => lines
    .filter(l => l.itemId && Number(l.qty) > 0)
    .map(l => ({ itemId: l.itemId, qty: Number(l.qty), uom: getItem(l.itemId)?.unit || '' }));

  const prefixMissing = !editId && !hasPrefix(state, 'stockIssue', form.date);

  const handleSave = () => {
    setSubmitted(true);
    if (prefixMissing) { alert('No Prefix Setting found for Stock Issue. Add one in Masters → Prefix Setting (effective on the selected date) before saving.'); return; }
    if (hasErrors) return;
    setSaving(true);
    try {
      const payload = {
        date: form.date,
        consumptionHead: form.consumptionHead,
        issuedTo: form.issuedTo,
        narration: form.narration,
        items: cleanLines(form.items),
      };
      if (editId) {
        dispatch({ type: 'UPDATE_STOCK_ISSUE', payload: { id: editId, ...payload } });
        addToast('Stock issue updated', 'success');
      } else {
        dispatch({ type: 'ADD_STOCK_ISSUE', payload });
        addToast('Stock issued for consumption', 'success');
      }
      closeForm();
    } catch (e) {
      addToast('Failed to save stock issue', 'error');
      setSaving(false);
    }
  };

  const openNew = () => { setEditId(null); setForm(emptyForm()); setSubmitted(false); setShowForm(true); };
  const openEdit = (row) => {
    setEditId(row.id);
    setForm({
      date: row.date,
      consumptionHead: row.consumptionHead || 'Stationary',
      issuedTo: row.issuedTo || '',
      narration: row.narration || '',
      items: (row.items || []).length ? row.items.map(l => ({ ...l })) : [emptyLine()],
    });
    setSubmitted(false); setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditId(null); setForm(emptyForm()); setSubmitted(false); setSaving(false); };

  const summarize = (lines) => (lines || []).map(l => `${itemName(l.itemId)} × ${l.qty}`).join(', ');

  const columns = [
    { key: 'issueNo', label: 'Issue No', sortable: true },
    { key: 'date', label: 'Date', render: v => formatDate(v), exportValue: r => formatDate(r.date) },
    { key: 'consumptionHead', label: 'Consumption Head', sortable: true, render: v => <span className="badge badge-warning">{v}</span> },
    { key: 'issuedTo', label: 'Issued To' },
    { key: 'items', label: 'Items Issued', render: v => summarize(v), exportValue: r => summarize(r.items) },
    { key: 'narration', label: 'Narration' },
  ];

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Stock Issue to Consumption</h1>
        <button onClick={openNew} className="btn btn-primary flex items-center gap-2">
          <Plus size={16} /> New Issue
        </button>
      </div>

      <div className="card">
        <DataTable
          columns={columns}
          data={[...state.stockIssues].reverse()}
          searchFields={['issueNo', 'consumptionHead', 'issuedTo', 'narration']}
          exportFilename="stock-issues"
          emptyState={{ title: 'No stock issues yet', description: 'Issue inventory items to a consumption head (stock will reduce).', actionLabel: 'New Issue', onAction: openNew }}
          actions={(row) => (
            <>
              <button onClick={() => setPrintDoc(row)} className="p-1.5 rounded hover:bg-blue-50 text-blue-600" title="Print"><Printer size={15} /></button>
              <button onClick={() => openEdit(row)} className="p-1.5 rounded hover:bg-amber-50 text-amber-600" title="Edit"><Pencil size={15} /></button>
              <button onClick={() => {
                if (window.confirm(`Delete issue ${row.issueNo}? Stock will be added back.`)) {
                  dispatch({ type: 'DELETE_STOCK_ISSUE', payload: row.id });
                  addToast('Stock issue deleted', 'success');
                }
              }} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Delete"><Trash2 size={15} /></button>
            </>
          )}
        />
      </div>

      <Modal isOpen={showForm} onClose={closeForm} title={editId ? 'Edit Stock Issue' : 'New Stock Issue'} size="lg">
        <ErrorSummary errors={errors} show={submitted && hasErrors} />

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Issue No">
              <input className="input bg-gray-100 font-semibold text-blue-700" readOnly
                value={editId ? state.stockIssues.find(s => s.id === editId)?.issueNo : previewDocNo(state, 'stockIssue', 'stockIssues', form.date, 'SI')} placeholder="Set Prefix first" />
            </FormField>
            <FormField label="Date" required error={errors.date} touched={submitted}>
              <input className="input" type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} max={today()} />
            </FormField>
            <FormField label="Consumption Head" required>
              <select className="input" value={form.consumptionHead} onChange={e => setForm({ ...form, consumptionHead: e.target.value })}>
                {CONSUMPTION_HEADS.map(h => <option key={h} value={h}>{h}</option>)}
              </select>
            </FormField>
            <FormField label="Issued To" hint="Person / department (optional)">
              <input className="input" value={form.issuedTo} onChange={e => setForm({ ...form, issuedTo: e.target.value })} maxLength={60} placeholder="e.g. Maintenance Dept" />
            </FormField>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="label mb-0">Items to Issue (Purchase Items)</label>
              <button type="button" onClick={addLine} className="btn btn-secondary btn-sm flex items-center gap-1"><Plus size={13} /> Add Row</button>
            </div>
            {submitted && errors.items && <p className="text-xs text-red-600 mb-2">{errors.items}</p>}
            {issuableItems.length === 0 && <p className="text-xs text-amber-600 mb-2">No purchase items available. Mark items as "Purchase Item" in Item Master.</p>}
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th className="text-right" style={{ width: 110 }}>Stock</th>
                    <th className="text-right" style={{ width: 110 }}>Qty</th>
                    <th style={{ width: 80 }}>UOM</th>
                    <th style={{ width: 50 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {form.items.map((l, i) => {
                    const sel = getItem(l.itemId);
                    return (
                      <tr key={i}>
                        <td>
                          <select className="input" value={l.itemId} onChange={e => setLine(i, { itemId: e.target.value })}>
                            <option value="">-- Select Item --</option>
                            {issuableItems.map(it => <option key={it.id} value={it.id}>{it.name}</option>)}
                          </select>
                          {submitted && errors[`item_${i}`] && <p className="text-xs text-red-600 mt-1">{errors[`item_${i}`]}</p>}
                        </td>
                        <td className="text-right text-gray-600">{sel ? `${sel.currentStock} ${sel.unit || ''}` : '-'}</td>
                        <td>
                          <input className="input text-right" type="number" min="0" value={l.qty}
                            onChange={e => setLine(i, { qty: Number(e.target.value) })} />
                        </td>
                        <td className="text-gray-600">{sel?.unit || '-'}</td>
                        <td className="text-center">
                          <button type="button" onClick={() => removeLine(i)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Remove row"><Trash2 size={14} /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <FormField label="Narration">
            <input className="input" value={form.narration} onChange={e => setForm({ ...form, narration: e.target.value })} maxLength={200} placeholder="e.g. Issued for office stationary" />
          </FormField>
        </div>

        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
          <button onClick={closeForm} className="btn btn-secondary">Cancel</button>
          <button data-keyboard-save onClick={handleSave} className="btn btn-primary" disabled={(submitted && hasErrors) || saving}>Save</button>
        </div>
      </Modal>

      {printDoc && <PrintPreview type="stockIssue" data={printDoc} onClose={() => setPrintDoc(null)} />}
    </div>
  );
}
